/**
 * Phone photos arrive as HEIC on iPhones. Browsers that can decode HEIC
 * (every iOS browser, Safari on the Mac) turn it into a JPEG here before it
 * is uploaded, so the file previews everywhere, including the admin, which
 * opens in Chrome and cannot show HEIC at all. Browsers that cannot decode
 * it get the original back and the upload goes ahead unchanged.
 *
 * Converted photos are also capped at MAX_EDGE pixels on the long side: a
 * 12 MP scan of a bill is far more than anyone needs to read it.
 */

const HEIC_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];
const MAX_EDGE = 2400;
const JPEG_QUALITY = 0.88;

export function isHeic(file: File): boolean {
  if (HEIC_TYPES.includes(file.type.toLowerCase())) return true;
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext === 'heic' || ext === 'heif';
}

/* Some iPhone photos come through named .jpg with type image/jpeg but are
   still HEIC inside (seen 12 Oct 2026, Sutter Health photos). Name and type
   can't be trusted, so look at the first bytes: HEIC files start with an
   "ftyp" box whose brand is one of these. */
const HEIC_BRANDS = ['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'mif1', 'msf1'];

export async function sniffHeic(file: File): Promise<boolean> {
  try {
    const head = new Uint8Array(await file.slice(0, 12).arrayBuffer());
    if (head.length < 12) return false;
    const text = String.fromCharCode(...head.slice(4, 12));
    return text.startsWith('ftyp') && HEIC_BRANDS.includes(text.slice(4, 8));
  } catch {
    return false;
  }
}

async function decode(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      /* fall through to the <img> path */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('decode failed'));
      img.src = url;
    });
  } finally {
    // The image keeps its pixels once loaded; the URL can go.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/**
 * Returns a JPEG File for HEIC input when the browser can decode it, else
 * the original file. Never throws.
 */
export async function prepareImageForUpload(file: File): Promise<File> {
  if (!isHeic(file) && !(await sniffHeic(file))) return file;
  try {
    const source = await decode(file);
    const width = 'naturalWidth' in source ? source.naturalWidth : source.width;
    const height = 'naturalHeight' in source ? source.naturalHeight : source.height;
    if (!width || !height) return file;

    const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    if ('close' in source) source.close();

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    if (!blob) return file;

    const name = file.name.replace(/\.(heic|heif|jpe?g)$/i, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified });
  } catch {
    return file;
  }
}
